/**
 * AccessLogsTable - Table + pagination for AccessLogsPage
 */
import React from 'react';
import { RefreshCw, LogIn, Calendar, User, Mail } from 'lucide-react';
import { AccessLog } from '../../services/accessLogService';
import { ActionBadge, StatusBadge, DeviceIcon } from './AccessLogBadges';

interface AccessLogsTableProps {
  logs: AccessLog[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onRowClick: (log: AccessLog) => void;
  onPrevPage: () => void;
  onNextPage: () => void;
  formatDate: (dateStr: string) => string;
}

export const AccessLogsTable: React.FC<AccessLogsTableProps> = ({
  logs,
  loading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onRowClick,
  onPrevPage,
  onNextPage,
  formatDate,
}) => {
  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Data/Hora</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Usuário</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Ação</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">IP</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Dispositivo</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Motivo Falha</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center">
                  <RefreshCw size={24} className="animate-spin mx-auto text-blue-600" />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Carregando logs...</p>
                </td>
              </tr>
            ) : logs.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                  <LogIn size={24} className="mx-auto mb-2 opacity-50" />
                  Nenhum log encontrado
                </td>
              </tr>
            ) : (
              logs.map((log) => (
                <tr
                  key={log.id}
                  onClick={() => onRowClick(log)}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    <div className="flex items-center gap-1">
                      <Calendar size={12} className="text-gray-400" />
                      {formatDate(log.data_hora)}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {log.usuario ? (
                      <div className="flex items-center gap-1">
                        <User size={12} className="text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">{log.usuario}</span>
                      </div>
                    ) : log.email_tentativa ? (
                      <div className="flex items-center gap-1">
                        <Mail size={12} className="text-red-400" />
                        <span className="text-sm text-red-600 dark:text-red-400">{log.email_tentativa}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <ActionBadge acao={log.acao} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge sucesso={log.sucesso} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-gray-300 font-mono">
                    {log.ip || '-'}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <DeviceIcon deviceType={log.device_info?.device_type} />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {log.device_info?.browser || 'Unknown'}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-red-600 dark:text-red-400 max-w-[200px] truncate">
                    {log.motivo_falha || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Mostrando {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalCount)} de {totalCount} registros
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onPrevPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Anterior
            </button>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Página {currentPage} de {totalPages}
            </span>
            <button
              onClick={onNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border dark:border-gray-600 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Próximo
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
